import React from 'react';
import Card from '../Card';
import MetricCardGrid from '../MetricCardGrid';
import DateRangeFilter from '../DateRangeFilter';
import DataTable, { TableColumn } from '../DataTable';
import ProgressBar from '../ProgressBar';
import Button from '../Button';
import { Download } from 'lucide-react';
import { TimeTrackingMetrics } from '../../utils/reportingUtils';

interface TimeTrackingTabProps {
  timeTrackingMetrics: TimeTrackingMetrics;
  dateRangeStart: string;
  dateRangeEnd: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onExportCSV: () => void;
}

interface ProjectHoursRow {
  projectId: string;
  projectName: string;
  hours: number;
  billableHours: number;
  billablePercent: number;
  distributionPercent: number;
}

interface UserHoursRow {
  userId: string;
  userName: string;
  hours: number;
  billableHours: number;
  billablePercent: number;
}

const TimeTrackingTab: React.FC<TimeTrackingTabProps> = ({
  timeTrackingMetrics,
  dateRangeStart,
  dateRangeEnd,
  onStartDateChange,
  onEndDateChange,
  onExportCSV
}) => {
  const summaryMetrics = [
    {
      value: timeTrackingMetrics.totalHours.toFixed(1),
      label: 'Total Hours Logged',
      color: 'primary' as const
    },
    {
      value: timeTrackingMetrics.billableHours.toFixed(1),
      label: 'Billable Hours',
      color: 'success' as const
    },
    {
      value: timeTrackingMetrics.nonBillableHours.toFixed(1),
      label: 'Non-Billable Hours',
      color: 'warning' as const
    },
    {
      value: timeTrackingMetrics.totalHours > 0 
        ? `${Math.round((timeTrackingMetrics.billableHours / timeTrackingMetrics.totalHours) * 100)}%`
        : '0%',
      label: 'Billable Rate',
      color: 'secondary' as const
    }
  ];

  const projectHoursData: ProjectHoursRow[] = timeTrackingMetrics.hoursByProject.map(project => ({
    projectId: project.projectId,
    projectName: project.projectName,
    hours: project.hours,
    billableHours: project.billableHours,
    billablePercent: project.hours > 0 ? (project.billableHours / project.hours) * 100 : 0,
    distributionPercent: timeTrackingMetrics.totalHours > 0 
      ? (project.hours / timeTrackingMetrics.totalHours) * 100 
      : 0
  }));

  const userHoursData: UserHoursRow[] = timeTrackingMetrics.hoursByUser.map(user => ({
    userId: user.userId,
    userName: user.userName,
    hours: user.hours,
    billableHours: user.billableHours,
    billablePercent: user.hours > 0 ? (user.billableHours / user.hours) * 100 : 0
  }));

  const projectColumns: TableColumn<ProjectHoursRow>[] = [
    {
      key: 'projectName',
      label: 'Project',
      render: (row) => (
        <span className="font-medium text-gray-900 dark:text-gray-100">{row.projectName}</span>
      )
    },
    {
      key: 'hours',
      label: 'Total Hours',
      render: (row) => (
        <span className="text-gray-700 dark:text-gray-300">{row.hours.toFixed(1)}h</span>
      )
    },
    {
      key: 'billableHours',
      label: 'Billable Hours',
      render: (row) => (
        <span className="text-success">{row.billableHours.toFixed(1)}h</span>
      )
    },
    {
      key: 'billablePercent',
      label: 'Billable %',
      render: (row) => (
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
          row.billablePercent >= 80 ? 'bg-success/15 text-success' :
          row.billablePercent >= 50 ? 'bg-warning/15 text-warning' :
          'bg-error/15 text-error'
        }`}>
          {row.billablePercent.toFixed(0)}%
        </span>
      )
    },
    {
      key: 'distribution',
      label: 'Distribution',
      render: (row) => (
        <ProgressBar percentage={row.distributionPercent} showLabel color="primary" />
      )
    }
  ];

  const userColumns: TableColumn<UserHoursRow>[] = [
    {
      key: 'userName',
      label: 'User',
      render: (row) => (
        <span className="font-medium text-gray-900 dark:text-gray-100">{row.userName}</span>
      )
    },
    {
      key: 'hours',
      label: 'Total Hours',
      render: (row) => (
        <span className="text-gray-700 dark:text-gray-300">{row.hours.toFixed(1)}h</span>
      )
    },
    {
      key: 'billableHours',
      label: 'Billable Hours',
      render: (row) => (
        <span className="text-success">{row.billableHours.toFixed(1)}h</span>
      )
    },
    {
      key: 'billablePercent',
      label: 'Billable %',
      render: (row) => (
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
          row.billablePercent >= 80 ? 'bg-success/15 text-success' :
          row.billablePercent >= 50 ? 'bg-warning/15 text-warning' :
          'bg-error/15 text-error'
        }`}>
          {row.billablePercent.toFixed(0)}%
        </span>
      )
    }
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Time Tracking Analytics</h2>
        <Button onClick={onExportCSV} variant="accent" leftIcon={Download}>
          Export to CSV
        </Button>
      </div>

      <DateRangeFilter
        startDate={dateRangeStart}
        endDate={dateRangeEnd}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
        className="mb-6"
      />

      <MetricCardGrid metrics={summaryMetrics} columns={4} className="mb-8" />

      <Card className="mb-6">
        <Card.Body>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Hours by Project</h3>
          <DataTable
            columns={projectColumns}
            data={projectHoursData}
            emptyMessage="No time entries for selected date range"
            getRowKey={(row) => row.projectId}
          />
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Hours by User</h3>
          <DataTable
            columns={userColumns}
            data={userHoursData}
            emptyMessage="No time entries for selected date range"
            getRowKey={(row) => row.userId}
          />
        </Card.Body>
      </Card>
    </div>
  );
};

export default TimeTrackingTab;
